import { Button,Input, Tabs, TabItem, CapsuleTab } from 'components/atoms/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { useState, useEffect } from 'react';
import TrophyBadgeCard from '@/components/organisms/Card/TrohyBadgeCard';
import { studentMypageApi } from '@/services/api';

interface TrophyBadgeData {
    id: number;
    trophyBadgeId: number;
    name: string;
    description?: string;
    iconUrl?: string;
    grade?: number;
    awardType: string;
    reason?: string;
    awardedAt: string;
}

interface GroupedTrophyBadge {
    trophyBadgeId: number;
    name: string;
    description?: string;
    iconUrl?: string;
    latestDate: string;
    count: number;
}

// 로그인 페이지
export default function StudentExamList() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);
    const [activeTab, setActiveTab] = useState<'badge' | 'trophy'>('trophy');
    const [trophies, setTrophies] = useState<TrophyBadgeData[]>([]);
    const [badges, setBadges] = useState<TrophyBadgeData[]>([]);
    const [loading, setLoading] = useState(true);
    
    const tabItems: TabItem[] = [
        { id: `${slugPrefix}/mypage/info`, label: '기본정보',onClick: () => navigate(`${slugPrefix}/mypage/info`) },
        { id: `${slugPrefix}/mypage/homework`, label: '숙제 채점표',onClick: () => navigate(`${slugPrefix}/mypage/homework`) },
        { id: `${slugPrefix}/mypage/exam`, label: '시험 성적표',onClick: () => navigate(`${slugPrefix}/mypage/exam`) },
        { id: `${slugPrefix}/mypage/trophy_badge`, label: '트로피 및 뱃지',onClick: () => navigate(`${slugPrefix}/mypage/trophy_badge`) },
      ];

    const achievementTabs: TabItem[] = [
        { id: 'trophy', label: '트로피', onClick: () => setActiveTab('trophy') },
        { id: 'badge', label: '뱃지', onClick: () => setActiveTab('badge') },
    ];

    useEffect(() => {
        loadTrophyBadgeList();
    }, []);

    const loadTrophyBadgeList = async () => {
        try {
            setLoading(true);
            const res = await studentMypageApi.getTrophyBadgeList();
            const data = res?.data || res;
            
            console.log('트로피/뱃지 데이터:', data);
            
            setTrophies(data?.trophies || []);
            setBadges(data?.badges || []);
        } catch (error) {
            console.error('트로피/뱃지 조회 실패:', error);
            setTrophies([]);
            setBadges([]);
        } finally {
            setLoading(false);
        }
    };

    // 같은 trophyBadgeId를 기준으로 그룹화
    const groupTrophyBadges = (items: TrophyBadgeData[]): GroupedTrophyBadge[] => {
        const grouped = new Map<number, GroupedTrophyBadge>();
        
        items.forEach(item => {
            const existing = grouped.get(item.trophyBadgeId);
            
            if (existing) {
                // 이미 존재하면 카운트 증가 및 최신 날짜 업데이트
                existing.count++;
                if (new Date(item.awardedAt) > new Date(existing.latestDate)) {
                    existing.latestDate = item.awardedAt;
                }
            } else {
                // 새로 추가
                grouped.set(item.trophyBadgeId, {
                    trophyBadgeId: item.trophyBadgeId,
                    name: item.name,
                    description: item.description,
                    iconUrl: item.iconUrl,
                    latestDate: item.awardedAt,
                    count: 1
                });
            }
        });
        
        // 최신 날짜 기준으로 정렬
        return Array.from(grouped.values()).sort((a, b) => 
            new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime()
        );
    };

    const groupedTrophies = groupTrophyBadges(trophies);
    const groupedBadges = groupTrophyBadges(badges);

  return (
    <main className='flex flex-1 pt-8 w-full bg-white md:my-10 md:justify-center card md:py-5 md:bg-transparent'>
        <div className='flex-1 pb-8 w-full'>
            <h1 className='px-5 pb-3 heading1'>마이페이지</h1>
            <Tabs tabs={tabItems} activeId={pathname}  />
            <div className='px-5 py-3'>
                <CapsuleTab tabs={achievementTabs} activeId={activeTab} />
                
                <section className='mt-6'>
                {loading ? (
                    <div className='text-center py-10 text-gray-500'>
                        로딩 중...
                    </div>
                ) : (
                    <>
                        {activeTab === 'trophy' && (
                            <div className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5'>
                                {groupedTrophies.length === 0 ? (
                                    <div className='col-span-2 md:col-span-3 lg:col-span-5 text-center py-10 text-gray-500'>
                                        트로피 수여 내역이 없습니다.
                                    </div>
                                ) : (
                                    groupedTrophies.map((trophy) => (
                                        <TrophyBadgeCard
                                            key={trophy.trophyBadgeId}
                                            type='trophy'
                                            name={trophy.name}
                                            description={trophy.description || ''}
                                            date={trophy.latestDate}
                                            iconUrl={trophy.iconUrl}
                                            count={trophy.count}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                        {activeTab === 'badge' && (
                            <div className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5'>
                                {groupedBadges.length === 0 ? (
                                    <div className='col-span-2 md:col-span-3 lg:col-span-5 text-center py-10 text-gray-500'>
                                        뱃지 수여 내역이 없습니다.
                                    </div>
                                ) : (
                                    groupedBadges.map((badge) => (
                                        <TrophyBadgeCard
                                            key={badge.trophyBadgeId}
                                            type='badge'
                                            name={badge.name}
                                            description={badge.description || ''}
                                            date={badge.latestDate}
                                            iconUrl={badge.iconUrl}
                                            count={badge.count}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                    </>
                )}
                </section>
            </div>
        </div>
    </main>
  )
}